import React, { useEffect, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import chunk from 'lodash/chunk'
import { useToasts } from 'react-toast-notifications'
import { isSuccessWithData, useFirebase } from 'src/firebase/useFirebase'
import CoachName from 'src/legacy/components/CoachName/CoachName'
import { describeFailure, Failure as ActionFailure } from '../api/failureReason'
import FailureMessage from '../components/FailureMessage'
import { useGetAdvancedAllTagsQuery } from '../api/advancedPatientTags.generated'
import { AdvancedApiError, advancedDelete, advancedPost } from '../api/client'
import { AdvancedCareTeamMember } from '../api/types'
import { useAdvancedResource } from '../api/useAdvancedResource'
import { useCoachRoster } from '../api/useCoachRoster'
import SectionCard from '../components/SectionCard'
import DialogHeader from '../components/DialogHeader'
import ProviderPicker from '../dashboard/careTeam/ProviderPicker'
import { ROLE_CATEGORIES, roleLabelFor } from '../dashboard/careTeam/roleCategories'
import { advancedColors } from '../theme'
import {
  AddResult,
  AdminPatient,
  CoachProfileShape,
  Failure,
  Outcome,
  advancedFailure,
  isKeptByStandard,
  isOnStandardCareteam,
  mapWithConcurrency,
  patientName,
} from './adminCareTeamBatch'
import {
  GetAdvancedAdminPatientsDocument,
  GetAdvancedAdminPatientsQuery,
  GetAdvancedAdminPatientsQueryVariables,
  useSearchAdvancedAdminPatientsQuery,
} from './adminCareTeams.generated'
import AdminPatientFilters, { AdminPatientFilter, EMPTY_FILTER, hasFilter } from './AdminPatientFilters'
import AdminPatientTable, { CareTeamLookup } from './AdminPatientTable'
import { adminFailureReason } from './adminFailureReason'
import { ADVANCED_PROGRAM_TAG_NAME } from './advancedProgramTag'

type Run = { kind: 'idle' } | { kind: 'running'; done: number; total: number } | { kind: 'done'; outcomes: Outcome[] }

const SEARCH_DELAY_MS = 300
const CONCURRENCY = 4
const FRESH_CHUNK = 200
const NAMES_SHOWN = 5

const FAILURE_REASONS: Record<Failure, string> = {
  standardRead: 'Their current Standard care teams couldn’t be read.',
  noAdminClaim: 'Your session has no admin claim. Sign out and back in as an admin.',
  serviceOutdated: 'This Advanced service has no admin routes yet: it’s older than this dashboard.',
  serviceDown: 'Advanced isn’t responding.',
  refused: 'Advanced refused the request.',
}

const careTeamPath = (uid: string) => `/admin/patients/${encodeURIComponent(uid)}/care-team`

const plural = (count: number) => `${count} ${count === 1 ? 'patient' : 'patients'}`

const nameList = (patients: AdminPatient[]) => {
  const names = patients.slice(0, NAMES_SHOWN).map(patientName).join(', ')

  return patients.length > NAMES_SHOWN ? `${names} and ${patients.length - NAMES_SHOWN} more` : names
}

const useDebounced = (value: string) => {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), SEARCH_DELAY_MS)

    return () => clearTimeout(timer)
  }, [value])

  return debounced
}

const RunOutcome = ({ run, onSelect }: { run: Run; onSelect: (patients: AdminPatient[]) => void }) => {
  if (run.kind === 'running') {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Adding {run.done} of {plural(run.total)}…
        </Typography>
        <LinearProgress variant="determinate" value={(run.done / run.total) * 100} />
      </Box>
    )
  }

  if (run.kind !== 'done') {
    return null
  }

  const withResult = (result: AddResult['result']) =>
    run.outcomes.filter((outcome) => outcome.result === result).map((outcome) => outcome.patient)
  const added = withResult('added')
  const notOnStandard = withResult('not_on_standard')
  const failedBy = (failure: Failure) =>
    run.outcomes.flatMap((outcome) =>
      outcome.result === 'failed' && outcome.failure === failure ? [outcome.patient] : []
    )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
      {notOnStandard.length > 0 && (
        <Alert severity="error">
          Skipped {plural(notOnStandard.length)} whose Standard care team doesn&apos;t include this provider:{' '}
          {nameList(notOnStandard)}. In the Participants tab, select these patients and use Change Careteam to add the
          provider first, otherwise they can&apos;t open the patient.
        </Alert>
      )}
      {(Object.keys(FAILURE_REASONS) as Failure[]).map((failure) => {
        const failed = failedBy(failure)

        return (
          failed.length > 0 && (
            <Alert
              key={failure}
              severity="error"
              action={
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => onSelect(failed)}
                  sx={{ borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Select them
                </Button>
              }
            >
              Failed to add to {plural(failed.length)}: {nameList(failed)}. {FAILURE_REASONS[failure]}
            </Alert>
          )
        )
      })}
      {added.length > 0 && <Alert severity="success">Added to {plural(added.length)}.</Alert>}
    </Box>
  )
}

const AdvancedAdminCareTeams = ({
  onTableVisibilityChange,
}: {
  onTableVisibilityChange?: (visible: boolean) => void
}) => {
  const client = useApolloClient()
  const coaches = useCoachRoster()
  const profiles = useFirebase<Record<string, CoachProfileShape>>('coachProfiles')
  const [filter, setFilter] = useState<AdminPatientFilter>(EMPTY_FILTER)
  const [selected, setSelected] = useState<Record<string, AdminPatient>>({})
  const [revealSelection, setRevealSelection] = useState(0)
  const [providerUid, setProviderUid] = useState<string | null>(null)
  const [role, setRole] = useState('nutritionist')
  const [run, setRun] = useState<Run>({ kind: 'idle' })
  const [ending, setEnding] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [pendingEnd, setPendingEnd] = useState<{ patient: AdminPatient; member: AdvancedCareTeamMember } | null>(null)
  const [endError, setEndError] = useState<ActionFailure | null>(null)
  const { addToast } = useToasts()
  const [refreshKey, setRefreshKey] = useState(0)
  const { data: tagsData } = useGetAdvancedAllTagsQuery()
  const appliedAdvancedProgramTag = useRef(false)

  const searched = { ...filter, query: useDebounced(filter.query).trim() }
  const search = useSearchAdvancedAdminPatientsQuery({
    variables: {
      query: searched.query,
      coachIDs: searched.coachIDs,
      includeOnlyPrimaryPatients: searched.primaryOnly,
      states: searched.states,
      sources: searched.sources,
      statesOfResidence: searched.statesOfResidence,
      tagIDs: searched.tagIDs,
    },
    skip: !hasFilter(searched),
  })
  const listed = (hasFilter(searched) && search.data?.searchPatientsV2.patients) || []
  const listedUids = listed.map((patient) => patient.uid)
  const tableVisible = expanded && hasFilter(searched) && !search.error && !!search.data && listed.length > 0

  useEffect(() => {
    onTableVisibilityChange?.(tableVisible)
  }, [onTableVisibilityChange, tableVisible])

  useEffect(() => {
    if (appliedAdvancedProgramTag.current || !expanded || !tagsData) return
    appliedAdvancedProgramTag.current = true
    const advancedTag = tagsData.patientTags.find((tag) => tag.name === ADVANCED_PROGRAM_TAG_NAME)
    if (!advancedTag) return
    setFilter((current) =>
      current.tagIDs.includes(advancedTag.id) ? current : { ...current, tagIDs: [advancedTag.id, ...current.tagIDs] }
    )
  }, [expanded, tagsData])

  const lookup = useAdvancedResource(
    () =>
      advancedPost<{ careTeams: Record<string, AdvancedCareTeamMember[]> }>('/admin/care-teams/lookup', {
        uids: listedUids,
      }),
    [listedUids.join(','), refreshKey],
    listed.length === 0
  )
  const careTeams: CareTeamLookup =
    lookup.status === 'ready' ? { status: 'ready', byUid: lookup.data.careTeams } : { status: lookup.status }

  const selectedPatients = Object.values(selected)
  const droppedByStandard =
    providerUid !== null && isSuccessWithData(profiles) && !isKeptByStandard(profiles.data[providerUid])

  const toggle = (patient: AdminPatient) =>
    setSelected(({ [patient.uid]: wasSelected, ...rest }) => (wasSelected ? rest : { ...rest, [patient.uid]: patient }))

  const toggleAll = () =>
    setSelected((current) =>
      listed.every((patient) => current[patient.uid])
        ? Object.fromEntries(Object.entries(current).filter(([uid]) => !listedUids.includes(uid)))
        : { ...current, ...Object.fromEntries(listed.map((patient) => [patient.uid, patient])) }
    )

  const selectOnly = (patients: AdminPatient[]) => {
    setSelected(Object.fromEntries(patients.map((patient) => [patient.uid, patient])))
    setRevealSelection((request) => request + 1)
  }

  // Fresh, not as listed, so an association added in Standard since the list loaded counts.
  const loadCurrent = async (uids: string[]): Promise<Record<string, AdminPatient>> => {
    const pages = await Promise.all(
      chunk(uids, FRESH_CHUNK).map((page) =>
        client.query<GetAdvancedAdminPatientsQuery, GetAdvancedAdminPatientsQueryVariables>({
          query: GetAdvancedAdminPatientsDocument,
          variables: { uids: page },
          fetchPolicy: 'network-only',
        })
      )
    )

    return Object.fromEntries(pages.flatMap(({ data }) => data.patients).map((patient) => [patient.uid, patient]))
  }

  // Checked, never written: an Advanced member without the association hits "required access" on every server query.
  const addTo = async (patient: AdminPatient, coachId: string): Promise<AddResult> => {
    if (!isOnStandardCareteam(patient, coachId)) {
      return { result: 'not_on_standard' }
    }
    try {
      await advancedPost(careTeamPath(patient.uid), { providerUid: coachId, role })
    } catch (error) {
      return { result: 'failed', failure: advancedFailure(error) }
    }

    return { result: 'added' }
  }

  const add = async () => {
    if (!providerUid) {
      return
    }

    const targets = selectedPatients
    setRun({ kind: 'running', done: 0, total: targets.length })
    const current = await loadCurrent(targets.map((patient) => patient.uid)).catch(() => undefined)
    const outcomes = await mapWithConcurrency(
      targets,
      CONCURRENCY,
      async (patient): Promise<Outcome> => {
        const fresh = current?.[patient.uid]

        return fresh
          ? { patient, ...(await addTo(fresh, providerUid)) }
          : { patient, result: 'failed', failure: 'standardRead' }
      },
      () => setRun((previous) => (previous.kind === 'running' ? { ...previous, done: previous.done + 1 } : previous))
    )
    setRun({ kind: 'done', outcomes })
    setRefreshKey((key) => key + 1)
    if (hasFilter(searched)) {
      search.refetch().catch(() => undefined)
    }
  }

  const dismissEnd = () => {
    if (ending) return
    setPendingEnd(null)
    setEndError(null)
  }

  const end = async () => {
    if (!pendingEnd || ending) return
    setEnding(true)
    setEndError(null)
    try {
      await advancedDelete(`${careTeamPath(pendingEnd.patient.uid)}/${encodeURIComponent(pendingEnd.member.memberId)}`)
      lookup.retry()
      setPendingEnd(null)
    } catch (error) {
      // Another admin ended it first: there is nothing left to retry.
      if (error instanceof AdvancedApiError && error.status === 404) {
        lookup.retry()
        setPendingEnd(null)
        addToast('That membership had already ended. The list now shows the current care team.', { appearance: 'info' })
        return
      }
      setEndError({ summary: 'Failed to end that membership.', reason: adminFailureReason(error) })
    } finally {
      setEnding(false)
    }
  }

  let results: React.ReactNode
  if (!hasFilter(searched)) {
    results = <Typography variant="body2">Set a filter to list patients.</Typography>
  } else if (search.error) {
    results = (
      <Alert severity="error">
        <FailureMessage failure={describeFailure('Failed to search patients.', search.error)} />
      </Alert>
    )
  } else if (!search.data) {
    results = <Typography variant="body2">Searching…</Typography>
  } else if (listed.length === 0) {
    results = <Typography variant="body2">No patients match these filters.</Typography>
  } else {
    const { patientCount } = search.data.searchPatientsV2
    results = (
      <>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {patientCount > listed.length
            ? `Showing ${listed.length} of ${patientCount} patients. Narrow the filters to reach the rest.`
            : `${plural(patientCount)} match.`}
        </Typography>
        <AdminPatientTable
          patients={listed}
          careTeams={careTeams}
          selected={selected}
          revealSelection={revealSelection}
          ending={ending}
          onToggle={toggle}
          onToggleAll={toggleAll}
          onEnd={(patient, member) => {
            setPendingEnd({ patient, member })
            setEndError(null)
          }}
        />
      </>
    )
  }

  return (
    <SectionCard
      title="Add or modify care teams"
      help="Add or modify providers on selected patients’ Advanced care teams. Note that the Advanced care team is not the same as the 'Standard' care team. In most - if not all - cases, providers should be added as a coach on the therapy/coaching side before adding them to the Advanced care team."
      collapsible
      onExpandedChange={setExpanded}
    >
      <Box
        sx={{
          '& .MuiFormControl-root': { mt: 0 },
        }}
      >
        <Typography variant="h4" sx={{ mb: 1.5 }}>
          Select patients to be assigned
        </Typography>
        <AdminPatientFilters filter={filter} onChange={setFilter} />
        <Box sx={{ mt: 2, color: advancedColors.inkMuted }}>{results}</Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 1 }}>
          <Typography variant="body2">{plural(selectedPatients.length)} selected</Typography>
          <Button size="small" disabled={selectedPatients.length === 0} onClick={() => setSelected({})}>
            Clear
          </Button>
        </Box>

        <Typography variant="h4" sx={{ mt: 3, mb: 1.5 }}>
          Select providers to be assigned to
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Box sx={{ width: 320, maxWidth: '100%' }}>
            <ProviderPicker
              label="Provider"
              options={coaches}
              value={providerUid}
              onChange={setProviderUid}
              helperText="The provider must already belong to each patient’s Therapy/Coaching care team."
            />
          </Box>
          <TextField
            select
            id="advanced-admin-care-team-role"
            label="Role"
            size="small"
            value={role}
            onChange={(event) => setRole(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            {ROLE_CATEGORIES.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            disabled={!providerUid || selectedPatients.length === 0 || run.kind === 'running'}
            onClick={add}
          >
            Add to {plural(selectedPatients.length)}
          </Button>
        </Box>
        <RunOutcome run={run} onSelect={selectOnly} />
        {droppedByStandard && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Standard drops providers who aren&apos;t an MD, an RD or a nutrition coach from the care team when a patient
            is accepted or restarted, which would also cut their access here.
          </Alert>
        )}
      </Box>
      <Dialog open={pendingEnd !== null} onClose={dismissEnd} maxWidth="xs" fullWidth>
        <DialogHeader title="End this membership?" onClose={dismissEnd} />
        <DialogContent>
          {pendingEnd && (
            <Typography variant="body2">
              End the {roleLabelFor(pendingEnd.member.role)} membership for{' '}
              <CoachName coachID={pendingEnd.member.providerUid} renderLabel={(name) => name} /> on{' '}
              {patientName(pendingEnd.patient)}&apos;s Advanced care team?
            </Typography>
          )}
          {endError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <FailureMessage failure={endError} />
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={dismissEnd} disabled={ending}>
            Cancel
          </Button>
          <Button onClick={end} color="error" variant="contained" disabled={ending}>
            End membership
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  )
}

export default AdvancedAdminCareTeams
